/**
 * The seventeen expense categories — THE ONE PLACE for the whole application.
 *
 * WHY THIS IS A CATALOGUE AND NOT A TABLE
 *
 * `docs/project-overview/expenses.md` AC-EX7 describes these as "pre-seeded at household creation",
 * and feature 009 removes that premise rather than implementing it. The list is identical for every
 * household, no member may add to it, and nothing about it varies per household — so there is
 * nothing to seed. There are no category rows anywhere, no domain event from `identity` at household
 * creation, and no SQL seed putting a product rule where it cannot say why.
 *
 * The baseline schema already agreed: it declares an `expense_category` enum and no category table of
 * any kind, which means AC-EX7 was never realisable without inventing a table for it.
 *
 * WHY `catalog/` AND NOT `value-objects/`
 *
 * This enforces no invariant beyond membership, so it is not a value object; and it is not a schema.
 * It is application-wide reference data, and naming the directory for what it is makes "the one
 * place" findable by someone who has only been told that it exists.
 *
 * WHY `@gladis/shared` AND NOT THE EXPENSES PACKAGE
 *
 * Feature 009's FR-023 requires that adding a category later touch neither the Expenses module nor
 * anything per household. `@gladis/shared` is the only home that satisfies "for the whole
 * application": `apps/app` depends on it, every domain package may import it, and `apps/api`
 * composes both.
 *
 * ADDING AN EIGHTEENTH CATEGORY
 *
 * Two changes, and no more: a line here, and `alter type expense_category add value '…'` in a
 * migration. The enum stays because the database keeps its own guard against a value the domain
 * never validated, consistent with every other classification in the baseline. Nothing under
 * `packages/domains/expenses` changes, and no household row is touched — and there is a test that
 * fails if a second hand-written copy of this list appears anywhere.
 */

/**
 * THE LABELS LEFT THIS FILE IN FEATURE 021, AND `value` STAYED.
 *
 * A label is a SENTENCE and `value` is an IDENTIFIER, so the two belong on opposite sides of that
 * feature's line. The kernel is the wrong side twice over: `apps/api` imports it and has no reader to
 * show a label to, and a Dutch member cannot read one written here.
 *
 * They live in `@gladis/ui/i18n`, keyed `category.expense.<value>`, in both languages. NOTHING ELSE
 * MOVED — the Zod enum below still derives from `value`, the `expense_category` database enum is
 * untouched, and every stored row means exactly what it meant.
 *
 * ADDING AN EIGHTEENTH CATEGORY IS STILL TWO CHANGES, as this file has always said. They are now a
 * line here plus a line in the migration, and a line in each of the two catalogue modules — which is
 * the same rule with the label counted where it actually lives. `catalogue-keys-resolve.test.ts`
 * fails if the two halves disagree.
 */
export const EXPENSE_CATEGORIES = [
  { value: "housing" },
  { value: "utilities" },
  { value: "groceries" },
  { value: "transport" },
  { value: "health" },
  { value: "insurance" },
  { value: "subscriptions" },
  { value: "dining_and_leisure" },
  { value: "clothing" },
  { value: "education" },
  { value: "travel" },
  { value: "hobbies" },
  { value: "personal_care" },
  { value: "pets" },
  { value: "home_and_garden" },
  { value: "gifts_and_donations" },
  { value: "other" },
] as const;

/**
 * The category identifiers, exactly the baseline `expense_category` enum's values.
 *
 * Derived from the array rather than written a second time. A hand-maintained union here would be
 * the first of the duplicates FR-022 exists to prevent, and it would be the one nobody suspects
 * because it sits in the same file as the source.
 */
export type ExpenseCategoryValue = (typeof EXPENSE_CATEGORIES)[number]["value"];

/**
 * Declaration order IS display order (FR-020).
 *
 * Not alphabetical: the list runs roughly from unavoidable to discretionary, which is the order a
 * household reads its own outgoings in. `other` is last because it is the fallback, and it is
 * otherwise treated exactly like the rest — no special case in any total or filter.
 */
export const EXPENSE_CATEGORY_VALUES: readonly ExpenseCategoryValue[] = EXPENSE_CATEGORIES.map(
  (category) => category.value,
);

/** The label for a category, for every picker, chip and row in the application. */
